import { Suspense, useEffect } from 'react';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import { DrawerPage } from 'components/DrawerPage';
import { Spin } from 'components/UI/Spin';
import { useHistory } from 'hooks/generic/useHistory';
import { useIsLoggedIn } from 'hooks/user/useIsLoggedIn';
import { useSearchParams } from 'hooks/useSearchParams';
import { useOpenCreateNftModal } from './hooks/useOpenCreateNftModal';
import { Configs } from '../../../constants/app/configs';
import { lazy } from 'utils/generic/lazy';
import {
  AccountQueryKey,
  EAccountRouteKeys
} from 'constants/account/account-route-keys';
import {
  ELsKeyAuthReachedFromOptions,
  ELsKeys
} from 'constants/app/storage-keys';

const { ProfileLayout } = lazy(
  () =>
    import(/* webpackChunkName: "ProfileLayout" */ './layouts/ProfileLayout')
);

const { Merchandise } = lazy(
  () => import(/* webpackChunkName: "Merchandise" */ './views/Merchandise')
);

const { Transactions } = lazy(
  () => import(/* webpackChunkName: "Transactions" */ './views/Transactions')
);

const { MyPayments } = lazy(
  () => import(/* webpackChunkName: "MyPayments" */ './views/MyPayments')
);

const { CreatedNfts } = lazy(
  () => import(/* webpackChunkName: "CreatedNfts" */ './views/CreatedNfts')
);

const { CollectedNfts } = lazy(
  () => import(/* webpackChunkName: "CollectedNfts" */ './views/CollectedNfts')
);

const { CollectionNftList } = lazy(
  () =>
    import(
      /* webpackChunkName: "CollectionNftList" */ './views/CreatedNfts/components/CollectionNftList'
    )
);

const { ProfileOverview } = lazy(
  () =>
    import(/* webpackChunkName: "ProfileOverview" */ './views/ProfileOverview')
);

const { CreateNft } = lazy(
  () => import(/* webpackChunkName: "CreateNft" */ './views/CreateNft')
);

const { CreateNftModal } = lazy(
  () =>
    import(/* webpackChunkName: "CreateNftModal" */ './views/CreateNftModal')
);

const { ListNftModal } = lazy(
  () => import(/* webpackChunkName: "ListNftModal" */ './views/ListNftModal')
);

const { NftMintSuccessModal } = lazy(
  () =>
    import(
      /* webpackChunkName: "NftMintSuccessModal" */ './components/NftMintSuccessModal'
    )
);

const { NftMintFailModal } = lazy(
  () =>
    import(
      /* webpackChunkName: "NftMintFailModal" */ './components/NftMintFailModal'
    )
);

const { PrintNftModal } = lazy(
  () =>
    import(/* webpackChunkName: "PrintNftModal" */ './components/PrintNftModal')
);

const { PrintNft } = lazy(
  () => import(/* webpackChunkName: "PrintNft" */ './components/PrintNft')
);

const { BecomeCreatorModal } = lazy(
  () =>
    import(
      /* webpackChunkName: "BecomeCreatorModal" */ './components/BecomeCreatorModal'
    )
);

const { ImportNftsModal } = lazy(
  () =>
    import(
      /* webpackChunkName: "ImportNftsModal" */ './components/ImportNftsModal'
    )
);

const MODALS = {
  [AccountQueryKey.modal.option.CHOOSE_CREATE_NFT]: CreateNftModal,
  [AccountQueryKey.modal.option.BECOME_CREATOR]: BecomeCreatorModal,
  [AccountQueryKey.modal.option.LIST_NFT]: ListNftModal,
  [AccountQueryKey.modal.option.MINT_SUCCESS_NFT]: NftMintSuccessModal,
  [AccountQueryKey.modal.option.MINT_FAIL_NFT]: NftMintFailModal,
  [AccountQueryKey.modal.option.CHOOSE_PRINT_NFT]: PrintNftModal,
  [AccountQueryKey.modal.option.PRINT_NFT]: PrintNft,
  ...(Configs.IS_CONNECT_METAMASK_AVAILABLE && {
    [AccountQueryKey.modal.option.IMPORT_NFTS]: ImportNftsModal
  }),
  default: () => null
};

export const Profile = () => {
  const match = useRouteMatch({ path: EAccountRouteKeys.ACCOUNT_PROFILE });
  const { modal } = useSearchParams();
  const { location } = useHistory();
  const isModalOpen = !!modal;
  const { isLoggedIn } = useIsLoggedIn();
  const { openCreateNftModal } = useOpenCreateNftModal();

  const isDrawerOpen =
    !!match &&
    !location.pathname.includes(
      EAccountRouteKeys.ACCOUNT_PROFILE_WALLET_CALLBACK
    );

  const ModalComponent = MODALS[modal || 'default'];

  /*
   * this effect handles storage values updates in case of user had journey with
   * non history redirections
   */
  useEffect(() => {
    if (!isLoggedIn) return;
    const authReachedFrom = JSON.parse(
      JSON.stringify(localStorage.getItem(ELsKeys.AUTH_REACHED_FROM)) || 'null'
    );
    if (authReachedFrom === ELsKeyAuthReachedFromOptions.CREATE_NFT) {
      const createNftModalOpened = openCreateNftModal({
        includeAccountPath: true
      });
      if (createNftModalOpened) {
        localStorage.removeItem(ELsKeys.AUTH_REACHED_FROM);
      }
    }
  }, [isLoggedIn, openCreateNftModal]);

  return (
    <DrawerPage open={isDrawerOpen} closable={false} closeIcon={null}>
      <Suspense fallback={<Spin type="absolute" size="large" />}>
        <Route path={EAccountRouteKeys.ACCOUNT_PROFILE}>
          <ProfileLayout>
            <Suspense fallback={null}>
              <ModalComponent isModalOpen={isModalOpen} />
            </Suspense>
            <Switch>
              <Redirect
                exact
                from={EAccountRouteKeys.ACCOUNT_PROFILE}
                to={EAccountRouteKeys.ACCOUNT_PROFILE_INFO}
              />
              <Route
                path={EAccountRouteKeys.ACCOUNT_PROFILE_INFO}
                component={ProfileOverview}
              />
              <Route
                path={`${EAccountRouteKeys.ACCOUNT_PROFILE_MY_NFTS_COLLECTION}/:id`}
                component={CollectionNftList}
                exact
              />
              <Route
                path={EAccountRouteKeys.ACCOUNT_PROFILE_MY_NFTS}
                component={CreatedNfts}
              />
              <Route
                path={EAccountRouteKeys.ACCOUNT_PROFILE_MY_NFTS_OWNED}
                component={CollectedNfts}
              />
              <Route
                path={[
                  EAccountRouteKeys.ACCOUNT_PROFILE_CREATE_NFT,
                  EAccountRouteKeys.ACCOUNT_PROFILE_EDIT_NFT
                ]}
                component={CreateNft}
                exact
              />
              <Route
                path={EAccountRouteKeys.ACCOUNT_PROFILE_TRANSACTIONS}
                component={Transactions}
                exact
              />
              <Route
                path={EAccountRouteKeys.ACCOUNT_PROFILE_MERCHANDISE}
                component={Merchandise}
                exact
              />
              <Route
                path={EAccountRouteKeys.ACCOUNT_PROFILE_MY_PAYMENTS}
                component={MyPayments}
                exact
              />
            </Switch>
          </ProfileLayout>
        </Route>
      </Suspense>
    </DrawerPage>
  );
};
